import { useEffect } from "react";

/**
 * Responsive Component
 * Wraps a responsive React DOM node and provided css classes as the element becomes larger.
 *
 * @component
 * @example (
 *  <ResponsiveComponent heights={{tall: 500}} widths={{fat: 500}}>
 *    <div>My Div</div>
 *  </ResponsiveComponent> )
 * @returns <div class="tall fat">My Div</div>
 *
 * @param props
 * @param children React DOM node
 * @param widths Object with css-classes for keys, and numbers as min-widths for values
 * @param heights Object with css-classes for keys, and numbers as min-widths for values
 */
function ResponsiveComponent({
  children,
  widths = {},
  heights = {},
  inlineSizes = {},
  blockSizes = {},
}) {
  let resizeObserver;
  useEffect(() => {
    if ("ResizeObserver" in self) {
      // TODO: use shared/resizeObserver
      resizeObserver = new ResizeObserver(function (children) {
        children.forEach(function (entry) {
          Object.keys(widths).forEach(function (breakpoint) {
            const minWidth = widths[breakpoint];
            if (entry.contentRect.width >= minWidth) {
              entry.target.classList.add(breakpoint);
            } else {
              entry.target.classList.remove(breakpoint);
            }
          });
          Object.keys(heights).forEach(function (breakpoint) {
            const minHeight = heights[breakpoint];
            if (entry.contentRect.height >= minHeight) {
              entry.target.classList.add(breakpoint);
            } else {
              entry.target.classList.remove(breakpoint);
            }
          });

          Object.keys(inlineSizes).forEach(function (breakpoint) {
            const size = inlineSizes[breakpoint];
            if (entry.borderBoxSize[0]?.inlineSize >= size) {
              entry.target.classList.add(breakpoint);
            } else {
              entry.target.classList.remove(breakpoint);
            }
          });
          Object.keys(blockSizes).forEach(function (breakpoint) {
            const size = blockSizes[breakpoint];
            if (entry.borderBoxSize[0]?.blockSize >= size) {
              entry.target.classList.add(breakpoint);
            } else {
              entry.target.classList.remove(breakpoint);
            }
          });
        });
      });

      resizeObserver.observe(children.ref.current);
    }
    return function cleanup() {
      resizeObserver?.disconnect();
    };
  });

  return children;
}

export default ResponsiveComponent;
